import './EasySoftwareWysiwygEditor.scss'

import Portal from '@material-ui/core/Portal'
import React, { FunctionComponent, useEffect, useRef, useState } from 'react'

import ContentAliasLinkPopup from '../ContentAliasLinkPopup/ContentAliasLinkPopup'
import { usePuxEditor } from '../PuxEditor/PuxEditorContext'
import type { SaveResult } from '../PuxEditor/PuxEditorState'

export interface EasySoftwareWysiwygEditorType {
  wysiwygId?: string
  className?: string
  inline?: boolean
  small?: boolean
  path?: string
  pageContentItemId?: string
  fieldId: string
  saveEditor: (value: string) => Promise<SaveResult>
}

const EasySoftwareWysiwygEditor: FunctionComponent<EasySoftwareWysiwygEditorType> = (
  props
) => {
  const [editor, setEditor] = useState<any>()
  const { store, session, toolbar } = usePuxEditor()
  const quillElement = useRef<HTMLDivElement>(null)
  const lastSelection = useRef<{ index: number; length: number } | null>(null)
  const [showContentLinkPopup, setShowContentLinkPopup] = useState<boolean>(false)
  const [selectionRange, setSelectionRange] = useState<any>(null)

  const classes = [`WysiwygEditor`]
  if (session?.error) classes.push(`WysiwygEditor--error`)
  classes.push(props.inline ? `WysiwygEditor--inline` : `WysiwygEditor--spaced`)
  if (props.className) {
    classes.push(props.className)
  }
  if (props.small) {
    classes.push(`WysiwygEditor--small`)
  }

  const currentActions = useRef({
    save: saveData,
    contentLink: handleOpenContentLinkPopup,
  })
  currentActions.current = {
    save: saveData,
    contentLink: handleOpenContentLinkPopup,
  }

  useEffect(
    () => store.register(props.fieldId, {
      save: () => currentActions.current.save(),
      contentLink: () => currentActions.current.contentLink(),
    }),
    [store, props.fieldId]
  )

  useEffect(() => {
    if (!toolbar) return
    const controller = new AbortController()
    let dispose: (() => void) | undefined
    initializeEditor(toolbar, controller.signal)
      .then((cleanup) => {
        if (controller.signal.aborted) cleanup?.()
        else dispose = cleanup
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          store.patch(props.fieldId, {
            error: `Unable to load content. Cancel and reopen to retry.`,
          })
        }
      })
    return () => {
      controller.abort()
      dispose?.()
    }
  }, [toolbar, store, props.fieldId])

  useEffect(() => {
    if (editor) editor.enable(!session?.saving && !session?.loading)
  }, [editor, session?.saving, session?.loading])

  function MakeNestedListIndented(list, type) {
    const li = document.createElement(`li`)
    if (type === `unordered`) {
      li.setAttribute(`data-list`, `bullet`)
    } else {
      li.setAttribute(`data-list`, `ordered`)
    }
    li.classList.add(`ql-indent-1`)
    li.innerHTML = list.querySelector(`li`)?.innerHTML ?? ""
    list.parentNode?.insertBefore(li, list.nextSibling)
    list.remove()
  }

  async function editHtml(signal: AbortSignal) {
    const response = await fetch(
      `${process.env.GATSBY_ORCHARD_API_URL}/puxapi/contentItem/getField?contentItemId=${encodeURIComponent(props.pageContentItemId ?? ``)}&path=${encodeURIComponent(props.path ?? ``)}`,
      { credentials: `include`, signal }
    )
    if (!response.ok) throw new Error(`Unable to load content`)
    const content = await response.text()

    if (content) {
      let htmlBody = ``
      htmlBody = content.replace(
        / src="\/media\//g,
        ` src="${process.env.GATSBY_ORCHARD_API_URL}/media/`
      )

      htmlBody = htmlBody.replace(
        / href="\/media\//g,
        ` href="${process.env.GATSBY_ORCHARD_API_URL}/media/`
      )

      htmlBody = htmlBody
        .replace(/font-size:\s[0-9]{1,}rem[;]{0,1}/g, ``)
        .replace(/font-family: -apple-system([^">]*)&quot;/g, ``)
        .replace(/font-family: -apple-system([^">]*)&quot;/g, ``)
        .replace(/pux-button\s/g, `Button Button--lg `)
        .replace(/pux-button-filled/g, `Button--primary`)
        .replace(/pux-button-outlined/g, `Button--border`)
        .replace(/pux-button-primary/g, ``)
        .replace(/pux-button-secondary/g, ``)

      htmlBody = htmlBody.replace(
        /(<ul>|<ol>|<\/li>)(?:[\s]+)(<li>|<\/ul>|<\/ol>)/g,
        `$1$2`
      )

      const htmlBodyDiv = document.createElement(`div`)
      htmlBodyDiv.innerHTML = htmlBody

      // Parse content alias links
      const contentAliasRegex = /\[contentaliaslink alias="([^"]*)" target="([^"]*)"\](.*?)\[\/contentaliaslink\]/g
      htmlBody = htmlBody.replace(contentAliasRegex, (match, alias, target, text) => {
        return `<contentalias alias="${alias}" target="${target}" data-contentalias="${alias}">${text}</contentalias>`
      })
      htmlBodyDiv.innerHTML = htmlBody

      // Format quill unordered lists

      htmlBodyDiv.querySelectorAll(`ul`).forEach((ul) => {
        ul.querySelectorAll(`:scope > li`).forEach((li) => {
          li.setAttribute(`data-list`, `bullet`)
          // Quill merges list containers; keep the bullet variant on each item.
          if (ul.classList.contains(`WysiwygList--bullet`)) {
            li.classList.add(`WysiwygList--bullet`)
          }
        })
      })

      // Fix paragraph breaking list

      htmlBodyDiv.querySelectorAll(`li p`).forEach((p) => {
        const li = p.parentElement
        if (li) {
          li.innerHTML = p.innerHTML
          if (p.className) li.classList.add(p.className)
        }
      })

      // Replace html font with colored span

      htmlBodyDiv.querySelectorAll(`font`).forEach((font) => {
        let newSpan = document.createElement("span");
        newSpan.innerHTML = font.innerText;
        newSpan.style.color = font.getAttribute("color") ?? "";
        font.insertAdjacentElement("afterend", newSpan);
        font.remove()
      })

      // Prepare nested lists for Quill

      htmlBodyDiv.querySelectorAll(`ul ul`).forEach((list) => {
        MakeNestedListIndented(list, `unordered`)
      })
      htmlBodyDiv.querySelectorAll(`ol ol`).forEach((list) => {
        MakeNestedListIndented(list, `ordered`)
      })
      htmlBodyDiv.querySelectorAll(`ul ol`).forEach((list) => {
        MakeNestedListIndented(list, `ordered`)
      })
      htmlBodyDiv.querySelectorAll(`ol ul`).forEach((list) => {
        MakeNestedListIndented(list, `unordered`)
      })

      htmlBody = htmlBodyDiv.innerHTML
        .replace(/\n\n/g, ``)
        .replace(/<p><\/p>/g, `<p> </p>`)

      return htmlBody
    }
  }

  async function initializeEditor(toolbarElement: HTMLDivElement, signal: AbortSignal) {
    const { default: Quill } = await import(`quill`)
    const html = await editHtml(signal)
    if (signal.aborted || !quillElement.current) return

    const Parchment = Quill.import(`parchment`)
    const Block = Quill.import(`blots/block`)
    const Inline = Quill.import(`blots/inline`)
    const BackgroundClass = Quill.import(`attributors/class/background`)
    const ColorClass = Quill.import(`attributors/class/color`)
    const SizeStyle = Quill.import(`attributors/style/size`)
    const TextAlignStyle = Quill.import(`attributors/style/align`)
    const Embed = Quill.import(`blots/embed`)
    const List = Quill.import(`formats/list`)
    const ListContainer = Quill.import(`formats/list`)

    const ClassBlockAttribute = new Parchment.Attributor(`class`, `class`, {
      scope: Parchment.Scope.BLOCK,
    })

    const ClassInlineAttribute = new Parchment.Attributor(`class`, `class`, {
      scope: Parchment.Scope.INLINE,
    })

    const DirBlockAttribute = new Parchment.Attributor(`dir`, `dir`, {
      scope: Parchment.Scope.BLOCK,
    })

    const DirInlineAttribute = new Parchment.Attributor(`dir`, `dir`, {
      scope: Parchment.Scope.INLINE,
    })

    Quill.register(BackgroundClass, true)
    Quill.register(ColorClass, true)
    Quill.register(SizeStyle, true)
    Quill.register(TextAlignStyle, true)

    Quill.register(ClassBlockAttribute)
    Quill.register(ClassInlineAttribute)

    Quill.register(DirBlockAttribute)
    Quill.register(DirInlineAttribute)

    /* Content Alias Link */

    class ContentAliasLinkBlot extends Inline {
      static blotName = `contentalias`
      static tagName = `contentalias`

      static create(value: any) {
        const node = super.create()
        node.setAttribute(`alias`, value.alias)
        node.setAttribute(`target`, value.target || ``)
        node.setAttribute(`data-contentalias`, value.alias)
        return node
      }

      static formats(node: HTMLElement) {
        return {
          alias: node.getAttribute(`alias`),
          target: node.getAttribute(`target`) || ``
        }
      }
    }

    class ExternalLinkBlot extends Inline {
      static blotName = `externallink`
      static tagName = `externallink`

      static create(value: any) {
        const node = super.create()
        node.setAttribute(`alias`, value.alias)
        node.setAttribute(`target`, value.target || ``)
        return node
      }

      static formats(node: HTMLElement) {
        return {
          alias: node.getAttribute(`alias`),
          target: node.getAttribute(`target`) || ``
        }
      }
    }

    Quill.register(ContentAliasLinkBlot)
    Quill.register(ExternalLinkBlot)

    /* Block */

    class BlockBlot extends Block {
      constructor(domNode) {
        super(domNode)
        domNode.setAttribute(`class`, ``)
        domNode.setAttribute(`dir`, ``)
        this.cache = {}
      }
    }

    BlockBlot.blotName = `block`

    class SoftLineBreakBlot extends Embed {
      static blotName = `softbreak`
      static tagName = `br`
    }

    Quill.register(SoftLineBreakBlot)

    /* Unordered list */

    class UListContainer extends ListContainer { }
    UListContainer.blotName = `ulist-container`
    UListContainer.tagName = `UL`
    UListContainer.className = `ul`

    class UListItem extends List {
      static register() {
        Quill.register(UListContainer)
      }

      static create(value: any) {
        const node = super.create()
        return node
      }
    }

    UListItem.blotName = `ulist`
    UListItem.tagName = `LI`
    UListItem.className = `LI`

    /* Ordered list */

    class OListContainer extends ListContainer { }
    OListContainer.blotName = `olist-container`
    OListContainer.tagName = `OL`
    OListContainer.className = `ol`

    class OListItem extends List {
      static register() {
        Quill.register(OListContainer)
      }

      static create(value: any) {
        const node = super.create()
        return node
      }
    }

    OListItem.blotName = `olist`
    OListItem.tagName = `LI`
    OListItem.className = `LI`

    UListItem.allowedChildren = [BlockBlot]
    OListItem.allowedChildren = [BlockBlot]
    UListContainer.allowedChildren = [
      UListItem,
      UListContainer,
      OListContainer,
      BlockBlot,
    ]
    OListContainer.allowedChildren = [
      OListItem,
      OListContainer,
      UListContainer,
      BlockBlot,
    ]

    Quill.register({
      'formats/list': List,
      'formats/ulist': UListItem,
      'formats/olist': OListItem,
    })

    /* Initialization */

    const newEditor = new Quill(quillElement.current, {
      debug: `warn`,
      modules: { toolbar: { container: toolbarElement }, table: false },
      placeholder: `Insert text…`,
      theme: `snow`,
    })
    newEditor.root.innerHTML = html ?? ``
    newEditor.update(`silent`)
    const initialHtml = newEditor.root.innerHTML
    const onTextChange = () => store.patch(props.fieldId, {
      dirty: newEditor.root.innerHTML !== initialHtml,
    })
    const onSelectionChange = (range: { index: number; length: number } | null) => {
      if (range) lastSelection.current = range
    }
    newEditor.on(`text-change`, onTextChange)
    newEditor.on(`selection-change`, onSelectionChange)
    toolbarElement.querySelectorAll(`.ql-picker`).forEach((picker) => {
      const label = picker.querySelector(`.ql-picker-label`)
      label?.setAttribute(
        `aria-label`,
        picker.classList.contains(`ql-header`) ? `Text style` : `Text alignment`
      )
    })
    setEditor(newEditor)
    store.patch(props.fieldId, { loading: false, error: null })
    newEditor.focus()
    return () => {
      newEditor.off(`text-change`, onTextChange)
      newEditor.off(`selection-change`, onSelectionChange)
      newEditor.disable()
    }
  }

  function handleOpenContentLinkPopup() {
    if (!editor) return

    const range = editor.getSelection() ?? lastSelection.current
    if (range && range.length > 0) {
      setSelectionRange(range)
      setShowContentLinkPopup(true)
    } else {
      alert('Please select text first')
    }
  }

  function handleContentLinkSelect(aliasOrUrl: string, isExternal?: boolean, openInNewTab?: boolean) {
    if (!editor || !selectionRange) return

    // Remove the selected text format first
    editor.removeFormat(selectionRange.index, selectionRange.length)

    // Apply the content alias link format
    if (isExternal) {
      editor.formatText(selectionRange.index, selectionRange.length, 'externallink', {
        alias: aliasOrUrl,
        target: openInNewTab ? '_blank' : '_self'
      })
    } else {
      editor.formatText(selectionRange.index, selectionRange.length, 'contentalias', {
        alias: aliasOrUrl,
        target: ''
      })
    }

    setShowContentLinkPopup(false)
    setSelectionRange(null)

    editor.setSelection(selectionRange.index, selectionRange.length)
    editor.focus()
  }

  function handleCloseContentLinkPopup() {
    setShowContentLinkPopup(false)
    setSelectionRange(null)
  }

  async function saveData(): Promise<SaveResult> {
    if (!editor) return { ok: false, error: `Content is still loading.` }
    let result = editor.root.innerHTML.trim()
    const htmlResult = document.createElement(`div`)
    htmlResult.innerHTML = result

    // Convert contentalias tags to the required format
    htmlResult.querySelectorAll(`contentalias`).forEach((element) => {
      const alias = element.getAttribute(`alias`)
      const target = element.getAttribute(`target`) || ``
      const text = element.textContent || ``
      const replacement = document.createTextNode(
        `[contentaliaslink alias="${alias}" target="${target}"]${text}[/contentaliaslink]`
      )
      element.parentNode?.replaceChild(replacement, element)
    })

    htmlResult.querySelectorAll(`externallink`).forEach((element) => {
      const alias = element.getAttribute(`alias`)
      const target = element.getAttribute(`target`) || ``
      const text = element.textContent || ``
      const replacement = document.createElement(`a`)
      replacement.setAttribute(`href`, alias ?? "")
      replacement.setAttribute(`target`, target)
      replacement.textContent = text
      element.parentNode?.replaceChild(replacement, element)
    })

    // Change ordered list to unordered list

    htmlResult
      .querySelectorAll(`li[data-list='bullet'].ql-indent-1`)
      .forEach((li) => {
        const list = document.createElement(`ul`)
        li.parentNode?.insertBefore(list, li.nextSibling)
        li.classList.remove(`ql-indent-1`)
        list.innerHTML = li.outerHTML
        li.remove()
      })

    // Change indent to nested list

    htmlResult
      .querySelectorAll(`li[data-list='ordered'].ql-indent-1`)
      .forEach((li) => {
        const list = document.createElement(`ol`)
        li.parentNode?.insertBefore(list, li.nextSibling)
        li.classList.remove(`ql-indent-1`)
        list.innerHTML = li.outerHTML
        li.remove()
      })
    htmlResult.querySelectorAll(`li[data-list='bullet']`).forEach((li) => {
      const ol = li.parentElement
      if (ol?.nodeName === `OL`) {
        const ul = document.createElement(`ul`)
        ul.innerHTML = ol.innerHTML
        ol.parentElement?.replaceChild(ul, ol)
      }
    })

    // Save editor data

    result = htmlResult.innerHTML
    return props.saveEditor(result)
  }

  return (
    <>
      <div className={classes.join(` `)}>
        <div ref={quillElement} id={`wysiwyg-${props.wysiwygId}`} className='Wysiwyg' />
      </div>
      {showContentLinkPopup && (
        <Portal>
          <div className="ContentAliasLinkPopup-backdrop" onClick={handleCloseContentLinkPopup} />
          <ContentAliasLinkPopup
            onSelect={handleContentLinkSelect}
            onClose={handleCloseContentLinkPopup}
          />
        </Portal>
      )}
    </>
  )
}

export default EasySoftwareWysiwygEditor
